/**
 * A utility function to convert a given object into a `FormData` instance.
 * It recursively handles nested objects, arrays, `File` objects, `Blob` objects, and primitive data types.
 */
export const createFormData = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  data: T
): FormData => {
  const formData = new FormData();

  const appendFormData = (formData: FormData, key: string, value: unknown): void => {
    if (value instanceof File) {
      formData.append(key, value);
    } else if (value instanceof Blob) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        appendFormData(formData, `${key}[${index}]`, item);
      });
    } else if (value && typeof value === 'object') {
      Object.keys(value).forEach(subKey => {
        appendFormData(formData, `${key}[${subKey}]`, value[subKey]);
      });
    } else if (value === null) {
      formData.append(key, '');
    } else if (typeof value === 'boolean') {
      formData.append(key, (value ? 1 : 0).toString());
    } else if (value !== undefined) {
      formData.append(key, value.toString());
    }
  };

  Object.keys(data).forEach(key => {
    appendFormData(formData, key, data[key]);
  });

  return formData;
};
